import styled from '@emotion/styled';
import { css } from '@emotion/react/macro';

import { Link } from 'react-router-dom';

import { breakpoints } from 'styles/basic';
import { linkWithUnderlineHover } from 'styles/mixins';

import Container from 'components/Container';

interface LangItemProps {
  active?: boolean | undefined;
}

interface ShowMenuProps {
  showMenu?: boolean | undefined;
}

const Wrapper = styled.header`
  position: relative;
  padding: 2rem 0;
  border-bottom: .6rem solid ${(props) => props.theme.mainColors.secondary};

  @media (min-width: ${breakpoints.smallTablet}) {
    padding: 3rem 0;
  }

  @media (min-width: ${breakpoints.portraitTablet}) {
    border-bottom: 0;
  }
`;

const LogoWrapper = styled.div`
  @media (min-width: ${breakpoints.smallLaptop}) {
    display: flex;
    align-items: center;
  }
`;

const LinkStyled = styled(Link)`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (min-width: ${breakpoints.portraitTablet}) {
    gap: 1.5rem;
  }
`;

const PageName = styled.span`
  font-family: ${(props) => props.theme.fontFamily.special};
  font-size: 2rem;
  color: ${(props) => props.theme.colorMono.black};
  white-space: nowrap;

  @media (min-width: ${breakpoints.portraitTablet}) {
    font-size: 3rem;
    letter-spacing: -2.2px;
  }

  @media (min-width: ${breakpoints.smallLaptop}) {
    font-size: 4rem;
  }
`;

const Logo = styled.img`
  width: 16rem;

  @media (min-width: ${breakpoints.portraitTablet}) {
    width: 20rem;
  }

  @media (min-width: ${breakpoints.smallLaptop}) {
    width: 30rem;
  }
`;

const SubTitle = styled.span`
  display: block;
  margin-top: 1rem;
  font-size: 1.6rem;
  font-weight: bold;
  color: ${(props) => props.theme.mainColors.primary};

  @media (min-width: ${breakpoints.portraitTablet}) {
    margin-top: 1.5rem;
    font-size: 2rem;
  }

  @media (min-width: ${breakpoints.smallLaptop}) {
    margin-top: unset;
    margin-left: 3rem;
    font-size: 2rem;
  }
`;

const StyledContainer = styled(Container)`
  display: flex;
  align-items: end;

  @media (min-width: ${breakpoints.portraitTablet}) {
    align-items: center;
  }
`;

const SideBar = styled.div`
  display: flex;
  flex-direction: column-reverse;
  align-items: flex-end;
  gap: 3rem;
  margin-left: auto;
  padding-left: 1rem;

  @media (min-width: ${breakpoints.portraitTablet}) {
    flex-direction: row;
    align-items: center;
  }
`;

const LangList = styled.ul`
  display: flex;
  align-items: center;
  margin-bottom: .3rem;
  margin-left: auto;
  list-style: none;

  li + li {
    &::before {
      content: '/';
      display: block;
      margin: 0 1rem;
      font-weight: 500;
    }
  }

  @media (min-width: ${breakpoints.portraitTablet}) {
    margin-bottom: 0;
  }
`;

const LangItem = styled.li`
  display: flex;
  align-items: center;
`;

const LangButton = styled.button<LangItemProps>`
  padding: 0 .5rem;
  ${(props) => linkWithUnderlineHover('&', 3, props.theme.mainColors.secondary)};

  ${(props) => props.active && css`
    &::after {
      width: 100%;
    }
  `};
`;

const HamburgerMenuButton = styled.button<ShowMenuProps>`
  position: absolute;
  top: 1.5rem;
  right: ${(props) => props.theme.layout.mobilePadding};
  z-index: 4;
  width: 3rem;
  height: 3rem;

  ${(props) => props.showMenu && css`
    position: fixed;
  `};

  @media (min-width: ${breakpoints.smallTablet}) {
    right: ${(props) => props.theme.layout.padding};
  }
`;

const HamburgerMenu = styled.span<ShowMenuProps>`
  position: absolute;
  top: 50%;
  left: 0;
  width: 3rem;
  height: .2rem;
  background: ${(props) => props.theme.colorMono.black};
  transition: all .5s ease-in-out;

  &::before, &::after {
    content: '';
    position: absolute;
    left: 0;
    width: 3rem;
    height: .2rem;
    background: ${(props) => props.theme.colorMono.black};
    transition: all .5s ease-in-out;
  }
  &::before {
    top: -1rem;
  }
  &::after {
    top: 1rem;
  }

  ${(props) => props.showMenu && css`
    background: transparent;

    &::before {
      top: 0;
      transform: rotate(135deg);
    }
    &::after {
      top: 0;
      transform: rotate(-135deg);
    }
  `};

  @media (min-width: ${breakpoints.portraitTablet}) {
    display: none;
  }
`;

export {
  Wrapper,
  LogoWrapper,
  LinkStyled,
  PageName,
  Logo,
  SubTitle,
  StyledContainer,
  SideBar,
  LangList,
  LangItem,
  LangButton,
  HamburgerMenuButton,
  HamburgerMenu
};
